import type { RouteLocationNormalized } from 'vue-router'
import axios from 'axios'
import useEvent from './event'
import useError from './error'
import useAccess from './access'
import useLocalStorage from './useLocalStorage'

export default function useAfterware(to: RouteLocationNormalized) {
  const { pageView } = useEvent()
  const routeName = to.name.toString()
  const { error, createError } = useError()
  const { userMergeSubscriptions, userMergeModules, userMergePermissions } = useAccess()
  const { subscriptions, moduleAccesses, moduleAccessesByStores, jwt, permissions, permissionsByStores } =
    useLocalStorage()

  const triggerPageView = () => {
    if (to.meta?.trackQuery) {
      pageView(routeName, to.query, to.query)
      return
    }
    pageView(routeName, to.query)
  }

  const requirePermissions = () => {
    error.value = null

    const subscriptionMeta = to.meta.subscription as string
    const permissionsMeta = to.meta?.permissions as []

    const hasSubscription = Object.keys(subscriptions.value).find((subscription) => {
      return subscription.includes(subscriptionMeta)
    })

    if (subscriptionMeta && !hasSubscription) {
      createError(402, subscriptionMeta)
      return true
    }

    if (permissionsMeta) {
      for (const permission of permissionsMeta) {
        if (!permissions.value[permission]) {
          createError(403, permission)
          return true
        }
      }
    }

    return true
  }

  const refreshModuleAccesses = () => {
    const isAuthenticatedRoute = to.matched.some((record) => record.meta.requiresAuth)
    if (!jwt.value || !isAuthenticatedRoute) return true

    Promise.all([axios.get('users/subscriptions'), axios.get('users/modules'), axios.get('users/permissions')])
      .then(([requestUserSubscriptions, requestUserModules, requestUserPermissions]) => {
        subscriptions.value = userMergeSubscriptions(requestUserSubscriptions.data.data)
        moduleAccesses.value = userMergeModules(requestUserModules.data.data)
        moduleAccessesByStores.value = requestUserModules.data.data
        permissions.value = userMergePermissions(requestUserPermissions.data.data)
        permissionsByStores.value = requestUserPermissions.data.data
      })
      .catch((err) => {
        console.log(err)
      })

    return true
  }

  const activatedAfterwares = [triggerPageView, requirePermissions, refreshModuleAccesses]

  return {
    activatedAfterwares,
    triggerPageView,
    requirePermissions,
    refreshModuleAccesses
  }
}
